import { PublicEventDetails } from "@ac-expo/api-types";
import { useEventAvailableEntries, useTicketing } from "@ac-expo/hooks";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
  useIsMobile,
  useToast,
} from "@ac-expo/ui-components";
import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import { useUserContext } from "@web-app/src/context/auth/user.context";
import { useMemo } from "react";
import {
  canUnlockGroupTickets,
  GROUP_TICKETS_TITLE,
  GroupUnlock,
  isGroupTicketType,
} from "./GroupUnlock";
import { TicketingList } from "./TicketingList";

interface TicketingProps {
  isTicketingModalOpen: boolean;
  setIsTicketingModalOpen: (open: boolean) => void;
  event: PublicEventDetails | undefined;
  eventSlug: string;
  isProfessional: boolean;
  onAddToCartClose: () => void;
  hideTrigger?: boolean;
  // Opened from the event page line: the email field is shown right away.
  isGroupUnlockRequested?: boolean;
}

function Ticketing({
  isTicketingModalOpen,
  setIsTicketingModalOpen,
  event,
  eventSlug,
  isProfessional,
  onAddToCartClose,
  hideTrigger = false,
  isGroupUnlockRequested = false,
}: TicketingProps) {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const { isAuthenticated, user } = useUserContext();

  const {
    availableEntries,
    availableTicketTypes,
    availableBadgeTypes,
    availableActivityTicketTypes,
  } = useEventAvailableEntries({
    event,
    isProfessional,
    additionalUserTypes: ["group"],
  });

  const {
    localCart,
    handleAddToCart,
    buyableTicketTypes,
    buyableBadgeTypes,
    buyableActivityTicketTypes,
    hasGroupPurchaseMinimumError,
    isPending,
  } = useTicketing({
    availableEntries,
    ticketTypes: availableTicketTypes,
    badgeTypes: availableBadgeTypes,
    activityTicketTypes: availableActivityTicketTypes,
    basketType: "store",
    isAuthenticated,
    onAddToCartMutationSuccess: () => {
      const labels =
        buyableBadgeTypes.length > 0
          ? buyableTicketTypes.length > 0
            ? "Billets et badges"
            : "Badges"
          : "Billets";
      toast({
        title: `${labels} ajoutés au panier`,
        description: `Les ${labels.toLowerCase()} ont été ajoutés à votre panier.`,
        duration: 1000,
        position: isMobile ? "top" : "bottom-left",
      });
      setIsTicketingModalOpen(false);
      onAddToCartClose();
    },
    onAddToCartMutationError: (err) => {
      toast({
        title: "Erreur",
        description:
          err instanceof Error ? err.message : "Une erreur est survenue",
      });
    },
  });

  // Group tickets are only returned to their members: list them apart from the regular prices.
  const { regularTicketTypes, groupTicketTypes } = useMemo(
    () => ({
      regularTicketTypes: buyableTicketTypes.filter(
        (entry) => !isGroupTicketType(entry.entryType)
      ),
      groupTicketTypes: buyableTicketTypes.filter((entry) =>
        isGroupTicketType(entry.entryType)
      ),
    }),
    [buyableTicketTypes]
  );

  const showGroupUnlock = canUnlockGroupTickets({
    event,
    isAuthenticated,
    user,
    ticketTypes: groupTicketTypes.map((entry) => entry.entryType),
  });

  const { localPricing } = localCart;
  const total = localPricing.total;
  const discount = localPricing.discount;

  return (
    <>
      <Dialog
        open={isTicketingModalOpen}
        onOpenChange={(open) => {
          setIsTicketingModalOpen(open);
          if (!open) {
            localCart.resetCart();
          }
        }}
      >
        {!hideTrigger ? (
          <DialogTrigger asChild>
            <div className="w-full px-6 pb-4 sm:p-0">
              <Button
                variant="blue"
                className="size-full px-4 py-3 font-semibold"
              >
                Accéder à la billetterie
              </Button>
            </div>
          </DialogTrigger>
        ) : null}
        <DialogContent
          className="flex size-full max-h-[calc(100vh-120px)] max-w-full flex-col gap-2 overflow-auto bg-white p-0 sm:max-w-[calc(100vw-20px)] md:max-w-[700px]"
          // Opened on the Group unlock: the email field focuses itself, the dialog must not
          // move the focus to its first ticket button.
          onOpenAutoFocus={(focusEvent) => {
            if (isGroupUnlockRequested) {
              focusEvent.preventDefault();
            }
          }}
        >
          <DialogHeader className="h-max bg-gray-50 p-6 text-2xl font-bold">
            <DialogTitle>Billetterie</DialogTitle>
          </DialogHeader>

          <DialogDescription className="hidden">
            Popup de sélection de billets et badges
          </DialogDescription>

          <div className="flex size-full flex-1 flex-col gap-4 overflow-y-auto bg-white p-6">
            <TicketingList
              type="ticket"
              buyableEntries={regularTicketTypes}
              localCart={localCart}
              buyer={event?.buyer}
            />
            <TicketingList
              type="ticket"
              title={GROUP_TICKETS_TITLE}
              buyableEntries={groupTicketTypes}
              localCart={localCart}
              buyer={event?.buyer}
            />
            {showGroupUnlock && event ? (
              <GroupUnlock
                event={event}
                eventSlug={eventSlug}
                defaultEditing={isGroupUnlockRequested}
              />
            ) : null}
            <TicketingList
              type="badge"
              buyableEntries={buyableBadgeTypes}
              localCart={localCart}
            />
            <TicketingList
              type="activity"
              buyableEntries={buyableActivityTicketTypes}
              localCart={localCart}
              buyer={event?.buyer}
            />
          </div>

          <DialogFooter className="flex w-full flex-row items-center !justify-between border-t border-t-gray-300 bg-white p-6">
            <div className="flex flex-col gap-1">
              <p className="text-2xl font-bold">
                {discount > 0 && (
                  <span className="text-sm text-gray-500 line-through">{`${(total + discount).toFixed(2)}€`}</span>
                )}
                <span>{` ${total.toFixed(2)}€`}</span>
              </p>
              <p className="text-sm font-medium text-gray-500">TOTAL</p>
            </div>
            <Button
              isLoading={isPending}
              variant="blue"
              // The minimum is already stated under the line it concerns: the button stays
              // out of reach instead of repeating it.
              disabled={hasGroupPurchaseMinimumError}
              onClick={handleAddToCart}
            >
              Ajouter au panier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Ticketing;
